import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

export interface NO4ReportParams {
  mailCategories: string[];
  accountingType: string;
  year: number;
  formatType: 'html' | 'pdf' | 'xls';
  direction: string;
  designatedOperator?: string;
}

// Types for NO4 Report data structure based on actual API response
export interface NO4ReportRow {
  summaryRowType: string;
  products: string[];
  activeCells: string[];
  summaryAlerts: any;
  originalItems: number | null;
  originalWeight: number | null;
  originalIpk: number | null;
  originalAdj: number | null;
  originalNet: number | null;
  bilateralItems: number | null;
  bilateralWeight: number | null;
  bilateralIpk: number | null;
  bilateralAdj: number | null;
  bilateralNet: number | null;
  bilateralItemsDeltaSign: string | null;
  bilateralWeightDeltaSign: string | null;
  bilateralIpkDeltaSign: string | null;
  bilateralAdjDeltaSign: string | null;
  bilateralNetDeltaSign: string | null;
  originalGrossIpk: number | null;
  bilateralGrossIpk: number | null;
  bilateralGrossIpkDeltaSign: string | null;
  calculatedBilateralItems: number | null;
  calculatedBilateralWeight: number | null;
  adj: number | null;
  calculatedBilateralGrossIPK: number | null;
  weight: number | null;
  items: number | null;
  net: number | null;
  ipk: number | null;
  grossIpk: number | null;
  productsLabel: string;
}

export interface NO4ReportData {
  no3Cn56Summary: {
    mailCategoryEnum: string;
    rows: NO4ReportRow[];
    sendingPpo: string;
    receivingPpo: string;
    year: number;
    accountingType: string;
    rowsByType: Record<string, NO4ReportRow>;
    key: string;
  };
  cn56ItemsAndNetWeightSummary: any;
  calculationNumberOfItemsByFormat: any;
  cn61Totals: any;
  hasAlerts: boolean;
}

export interface NO4ReportResponse {
  // For HTML format, contains the table data
  data?: NO4ReportData[];
  url?: string;
  content?: Blob;
  filename?: string; // Add filename field for PDF/XLS downloads
}

export function useNO4Report() {
  return useMutation<NO4ReportResponse, Error, NO4ReportParams>({
    mutationFn: async (params: NO4ReportParams) => {
      // Create URLSearchParams to handle array parameters without brackets
      const searchParams = new URLSearchParams();
      
      // Add mail categories as multiple individual parameters
      params.mailCategories.forEach(category => {
        searchParams.append('mailCategories', category);
      });
      
      // Add accounting type (single string)
      searchParams.append('accountingType', params.accountingType);
      
      // Add year (integer)
      searchParams.append('year', params.year.toString());
      
      // Add format type
      searchParams.append('formatType', params.formatType);
      
      // Add direction (required) - convert to uppercase
      searchParams.append('direction', params.direction.toUpperCase());
      
      // Add optional parameters
      if (params.designatedOperator) {
        searchParams.append('designatedOperator', params.designatedOperator);
      }

      const response = await axios.get(`/reports/no4?${searchParams.toString()}`, {
        // HTML returns JSON with table data, PDF/XLS return binary files
        responseType: params.formatType === 'html' ? 'json' : 'blob',
      });

      // For HTML format, return the table data
      if (params.formatType === 'html') {
        return { data: response.data };
      }

      // For PDF/XLS, return the blob for file download
      let fileName: string | null = null;
      
      // Try to extract the filename from the Content-Disposition header
      const contentDisposition = 
        response.headers['content-disposition'] || 
        response.headers['Content-Disposition'];
      
      if (contentDisposition) {
        // Match the filename in Content-Disposition header
        const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
        if (filenameMatch && filenameMatch[2]) {
          fileName = decodeURIComponent(filenameMatch[2]);
        }
      }
      
      // If we couldn't get the filename from the header, generate one
      if (!fileName) {
        // Extract the current date in YYYYMMDD format for filename
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const timestamp = String(date.getHours()).padStart(2, '0') + 
                          String(date.getMinutes()).padStart(2, '0') + 
                          String(date.getSeconds()).padStart(2, '0');
        
        // Format filename with timestamp
        fileName = `NO4_Report_${year}${month}${day}_${timestamp}.${params.formatType}`;
      }
      
      const mimeType = params.formatType === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { 
        content: response.data,
        filename: fileName
      };
    },
  });
}